/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable no-console */
import { createContext, useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';
import {
  getDisplayName,
  getUser,
  init,
  logout as oidcLogout,
  refreshToken,
  clearUser,
  signin,
} from './oidcClient';
/**
 * AuthContextValues defines the structure for the default values of the {@link AuthContext}.
 */
interface AuthContextValues {
  /**
   * Whether or not a user is currently authenticated
   */
  isAuthenticated: boolean;
  /**
   * The name of the authenticated user
   */
  username: string;
  /**
   * The email of the authenticated user
   */
  email: string;
  /**
   * Function to initiate the logout
   */
  logout: () => Promise<void>;
}

/**
 * Default values for the {@link AuthContext}
 */
const AuthContextData: AuthContextValues = {
  isAuthenticated: false,
  username: '',
  email: '',
  logout: () => Promise.resolve(),
};

/**
 * Create the AuthContext using the default values.
 */
export const AuthContext = createContext<AuthContextValues>(
  AuthContextData,
);

/**
 * The props that must be passed to create the {@link AuthContextProvider}.
 */
interface AuthContextProviderProps {
  /**
   * The elements wrapped by the auth context.
   */
  children: JSX.Element;
}

/**
 * AuthContextProvider is responsible for managing the authentication state of the current user.
 *
 * @param props
 */
const AuthContextProvider = (props: AuthContextProviderProps) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let mounted = true;
    getUser()
      .then((currentUser) => {
        if (mounted) setUser(currentUser);
      })
      .catch(() => {
        if (mounted) setUser(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Initiate the logout
   */
  const logout = async () => {
    setUser(null);
    await oidcLogout();
  };

  // Setup the context provider
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: Boolean(user && !user.expired),
        username: user ? getDisplayName(user) : '',
        email: user && typeof user.profile.email === 'string' ? user.profile.email : '',
        logout,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

const initAuthContextProvider = async () => {
  try {
    await init();
  } catch (err) {
    console.error(err);
    await clearUser();
    await signin();
  }
};

export { AuthContextProvider, initAuthContextProvider, refreshToken };
